import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

export default function Blog() {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6" data-testid="blog-page-title">
            {t('blog.title')}
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto" data-testid="blog-page-subtitle">
            {t('blog.subtitle')}
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12" data-testid="blog-loading">
            <p className="text-muted-foreground">Loading articles...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-12" data-testid="blog-empty">
            <p className="text-muted-foreground">No articles available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog, index) => (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden card-hover h-full flex flex-col" data-testid={`blog-card-${blog.id}`}>
                  <img
                    src={blog.image_url}
                    alt={i18n.language === 'en' ? blog.title_en : blog.title_as}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        <span>{blog.author}</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-3">
                      {i18n.language === 'en' ? blog.title_en : blog.title_as}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-3">
                      {i18n.language === 'en' ? blog.content_en.substring(0, 120) + '...' : blog.content_as.substring(0, 120) + '...'}
                    </p>
                    <Link to={`/blog/${blog.id}`}>
                      <Button variant="ghost" size="sm" className="w-full" data-testid={`blog-read-more-${blog.id}`}>
                        {t('blog.readMore')}
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
